import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Mail, 
  AlertTriangle,
  HardDrive,
  Shield,
  CheckCircle2,
  XCircle,
  Play,
  ExternalLink
} from 'lucide-react';

const checklist = [
  { icon: XCircle, text: 'Stop using the device.', warning: true },
  { icon: XCircle, text: 'Do not run repair utilities.', warning: true },
  { icon: XCircle, text: 'Do not open the device.', warning: true },
  { icon: CheckCircle2, text: 'Contact NPM for next steps.', warning: false }
];

// YouTube Video Component with click-to-play and lazy loading
const YouTubeEmbed = ({ videoId, thumbnailUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
      data-testid="video-container"
    >
      {!isPlaying ? (
        <button
          onClick={handlePlay}
          className="absolute inset-0 w-full h-full group cursor-pointer"
          aria-label="Play video"
          data-testid="video-play-button"
        >
          {/* Thumbnail */}
          {isVisible && (
            <img
              src={thumbnailUrl}
              alt="DriveSavers Data Recovery Video"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
            </div>
          </div>
        </button>
      ) : (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
          title="DriveSavers Data Recovery Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  );
};

const DataRecoveryPage = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e) => setIsMobile(!e.matches);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  return (
    <div className="relative overflow-hidden" data-testid="data-recovery-page">
      {/* Hero Section */}
      <section className="relative hero-gradient section-padding-hero" data-testid="data-recovery-hero">
        <div className="abstract-shape abstract-shape-1"></div>
        
        <div className="site-container relative">
          <div className="max-w-3xl prose-width">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3f3f3f] leading-[1.15] mb-5">
              Data Recovery
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              Lost access to business or personal data? NPM coordinates secure data recovery through a specialized recovery partner, including intake guidance, case coordination, and secure return of recovered data.
            </p>
          </div>
        </div>
      </section>

      {/* DriveSavers Partner Section - with background */}
      <section 
        className="section-padding section-bg-wrapper mobile-bg-purple" 
        data-testid="drivesavers-section"
      >
        {/* Background layer */}
        {!isMobile && (
          <div 
            className="section-bg-layer"
            style={{ backgroundImage: 'url(/assets/data-recovery-bg.jpg)' }}
            aria-hidden="true"
          />
        )}
        <div className="site-container section-bg-content">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Authorized DriveSavers Partner
            </h2>
            <p className="text-lg lg:text-xl text-black max-w-2xl mx-auto">
              Professional data recovery for businesses and individuals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            {/* Left Column - Partner Info Card */}
            <div className="npm-card p-6 lg:p-8">
              <div className="w-14 h-14 rounded-full bg-[#7dc242]/10 flex items-center justify-center mb-5">
                <HardDrive className="w-7 h-7 text-[#7dc242]" />
              </div>
              <p className="text-[0.9375rem] lg:text-base text-gray-600 leading-relaxed mb-6">
                Through a special agreement with DriveSavers, NPM provides a fast, reliable, and secure solution to data loss at a discounted rate. DriveSavers has recovered data since 2003. Their work does not void the original warranty on your storage device and is recommended by leading manufacturers.
              </p>
              
              {/* Authorized Reseller Badge - cleaner integration */}
              <a 
                href="https://drivesaversdatarecovery.com/welcome/thank-you/?dsNum=14045"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors group"
                data-testid="drivesavers-badge-link"
              >
                <img 
                  src="/assets/drivesavers-authorized-reseller.png" 
                  alt="DriveSavers Authorized Reseller"
                  className="w-16 h-auto"
                />
                <div className="text-left">
                  <span className="text-sm font-medium text-[#3f3f3f] block">Authorized Reseller</span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    View discounts <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </a>
            </div>

            {/* Right Column - Video */}
            <div className="w-full">
              <YouTubeEmbed 
                videoId="GmLIizFR8tE"
                thumbnailUrl="https://i.ytimg.com/vi/GmLIizFR8tE/maxresdefault.jpg"
              />
              <p className="mt-3 text-sm text-gray-500 text-center">
                Learn how DriveSavers recovers your data
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What To Do First Section */}
      <section className="section-padding bg-white" data-testid="checklist-section">
        <div className="site-container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
                What to Do First
              </h2>
              <p className="text-base text-gray-500">
                If you've experienced data loss, follow these steps
              </p>
            </div>
            
            <div className="space-y-4">
              {checklist.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-4 p-4 rounded-xl border ${
                    item.warning 
                      ? 'border-red-200 bg-red-50/50' 
                      : 'border-green-200 bg-green-50/50'
                  }`}
                  data-testid={`checklist-${index}`}
                >
                  <item.icon className={`w-6 h-6 flex-shrink-0 ${
                    item.warning ? 'text-red-500' : 'text-[#7dc242]'
                  }`} />
                  <span className={`text-[0.9375rem] font-medium ${
                    item.warning ? 'text-red-700' : 'text-[#3f3f3f]'
                  }`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Urgent CTA Section */}
      <section className="section-padding bg-red-50" data-testid="urgent-cta-section">
        <div className="site-container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Lost Access to Your Data?
            </h2>
            <p className="text-base lg:text-lg text-gray-600 mb-6">
              Time is critical in data recovery situations. Contact us immediately.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white border-0 h-11 px-6 text-[0.9375rem]" 
                asChild 
                data-testid="urgent-cta-phone"
              >
                <a href="tel:+17027695422">
                  <Phone className="w-[18px] h-[18px] mr-2" />
                  Call (702) 769-5422 Right Away
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-red-300 text-red-600 hover:bg-red-50 h-11 px-6 text-[0.9375rem]" 
                asChild 
                data-testid="urgent-cta-email"
              >
                <a href="mailto:david@applevar.com">
                  <Mail className="w-[18px] h-[18px] mr-2" />
                  david@applevar.com
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataRecoveryPage;
